import React, { useState, useEffect, useMemo, useCallback } from "react";
import Swal from "sweetalert2";
import Select, { components } from "react-select";



import { fetchWorkers } from "../../api/workerApi";
import { fetchVarieties } from "../../api/varietyApi";
import { fetchLengths } from "../../api/lengthApi";
import { fetchHarvestTypes } from "../../api/harvestTypeApi";
import { fetchBunchSizes, createCapture } from "../../api/captureApi";
import { getSessionStatus } from "../../api/sessionApi";
import api from "../../api/api";

import "../../styles/CapturePage.css";

const textSelectComponents = {
  DropdownIndicator: () => null,
  IndicatorSeparator: () => null,
  ClearIndicator: (props) => <components.ClearIndicator {...props}><span className="bunch-clear">Clear</span></components.ClearIndicator>,
};

// ── Empty form shape ──────────────────────────────────────────
const EMPTY_FORM = {
  worker:              "",
  variety_location_id: "",
  variety:             "",
  length:              "",
  harvest_type:        "",
  bunch_size:          "",
  bunch_count:         "",
};

const CapturePage = () => {
  // ── Session ────────────────────────────────────────────────
  const [sessionOpen,     setSessionOpen]     = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [loading,         setLoading]         = useState(true);

  // ── Raw data ───────────────────────────────────────────────
  const [workers,      setWorkers]      = useState([]);
  const [varieties,    setVarieties]    = useState([]);
  const [lengths,      setLengths]      = useState([]);
  const [harvestTypes, setHarvestTypes] = useState([]);
  const [bunchSizes,   setBunchSizes]   = useState([]);

  // ── Farm ───────────────────────────────────────────────────
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [showFarmSelector, setShowFarmSelector] = useState(true);
  const [bunchingStart, setBunchingStart] = useState(null);
  const [startTimeInput, setStartTimeInput] = useState("");
  const [startBusy, setStartBusy] = useState(false);

  // ── Form ───────────────────────────────────────────────────
  const [data,        setData]        = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastCapture,  setLastCapture]  = useState(null);
  const [successPulse, setSuccessPulse] = useState(false);

  useEffect(() => {
    if (!selectedFarm?.name) return;
    let cancelled = false;
    setBunchingStart(null);
    api.get("core/bunching-day-start/", { params: { farm: selectedFarm.name } })
      .then(({ data: status }) => { if (!cancelled) setBunchingStart(status); })
      .catch(() => { if (!cancelled) setBunchingStart({ error: true }); });
    return () => { cancelled = true; };
  }, [selectedFarm?.name]);

  const lockBunchingStart = async () => {
    if (!startTimeInput || !selectedFarm?.name) return;
    setStartBusy(true);
    try {
      const day = new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Nairobi", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
      const { data: status } = await api.post("core/bunching-day-start/", { farm: selectedFarm.name, day, start_time: startTimeInput });
      setBunchingStart(status);
      Swal.fire({ title: "Start time locked", text: "This farm’s bunching start time is locked for today.", confirmButtonColor: "#a92f6f" });
    } catch (error) {
      if (error.response?.status === 409 && error.response.data?.starts_at) {
        setBunchingStart({ locked: true, starts_at: error.response.data.starts_at });
        return;
      }
      Swal.fire("Could not lock start time", error.response?.data?.start_time?.[0] || error.response?.data?.detail || "Check your connection and try again.");
    } finally { setStartBusy(false); }
  };

  // =====================
  // LOAD DATA
  // =====================
  useEffect(() => {
    const loadData = async () => {
      try {
        const session = await getSessionStatus();
        setSessionOpen(session.is_open);
        setActiveSessionId(session.id || null);

        const [workersData, varietiesData, lengthsData, harvestData, bunchData] =
          await Promise.all([
            fetchWorkers().catch(() => []),
            fetchVarieties().catch(() => []),
            fetchLengths().catch(() => []),
            fetchHarvestTypes().catch(() => []),
            fetchBunchSizes().catch(() => []),
          ]);

        setWorkers(workersData);
        setVarieties(varietiesData);
        setLengths(lengthsData);
        setHarvestTypes(harvestData);
        setBunchSizes(bunchData);
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Failed to load initial data.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // =====================
  // FARM EXTRACTION
  // =====================
  const farms = useMemo(() => {
    const farmMap = new Map();

    workers.forEach((w) => {
      if (w.farm) {
        const key = w.farm_id || w.farm;
        if (!farmMap.has(key)) {
          farmMap.set(key, { id: key, name: w.farm, workerCount: 0, locationCount: 0 });
        }
        farmMap.get(key).workerCount++;
      }
    });

    varieties.forEach((v) => {
      if (v.locations_detail) {
        v.locations_detail.forEach((loc) => {
          const key = loc.farm_name || loc.farm_id;
          if (key) {
            if (!farmMap.has(key)) {
              farmMap.set(key, {
                id: key,
                name: loc.farm_name || `Farm ${key}`,
                workerCount: 0,
                locationCount: 0,
              });
            }
            farmMap.get(key).locationCount++;
          }
        });
      }
    });

    return Array.from(farmMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [workers, varieties]);

  // =====================
  // FILTERED OPTIONS BY FARM
  // =====================
  const filteredWorkers = useMemo(() => {
    if (!selectedFarm) return [];
    return workers.filter((w) => {
      const id = w.farm_id || w.farm;
      return String(id) === String(selectedFarm.id) || w.farm === selectedFarm.name;
    });
  }, [workers, selectedFarm]);

  // ── DISTINCT VARIETIES COMBINED WITH GHSE ──────────────────
  const filteredVarietyLocations = useMemo(() => {
    if (!selectedFarm) return [];
    
    const seen = new Set();
    const locations = [];

    varieties.forEach((variety) => {
      if (variety.locations_detail) {
        variety.locations_detail.forEach((loc) => {
          const locFarmKey = loc.farm_name || loc.farm_id;
          if (
            String(locFarmKey) === String(selectedFarm.id) ||
            loc.farm_name === selectedFarm.name
          ) {
            const ghseName = loc.ghse_name || `GHSE ${loc.ghse_id}`;
            const uniqueKey = `${variety.name}-${ghseName}`;
            
            if (seen.has(uniqueKey)) return;
            seen.add(uniqueKey);

            locations.push({
              location_id:   loc.id,
              variety_id:    variety.id,
              name:          variety.name,
              stems_per_net: variety.stems_per_net,
              farm_id:       loc.farm_id,
              zone_id:       loc.zone_id,
              ghse_id:       loc.ghse_id,
              zone_name:     loc.zone_name,
              ghse_name:     ghseName,
              displayLabel:  `${variety.name} — ${ghseName}`,
            });
          }
        });
      }
    });

    return locations.sort((a, b) => a.displayLabel.localeCompare(b.displayLabel));
  }, [varieties, selectedFarm]);

  // =====================
  // SELECT OPTIONS
  // =====================
  const workerOptions = useMemo(
    () => filteredWorkers.map((w) => ({
      value: w.id,
      label: `${w.payroll_number} — ${w.full_name}`,
      sub:   w.village || "",
    })),
    [filteredWorkers]
  );

  const varietyOptions = useMemo(
    () => filteredVarietyLocations.map((loc) => ({
      value:         loc.location_id,
      variety_id:    loc.variety_id,
      label:         loc.displayLabel,
      sub:           `${loc.zone_name || `Zone ${loc.zone_id}`}`,
      stems:         loc.stems_per_net,
      location_data: loc,
    })),
    [filteredVarietyLocations]
  );

  const lengthOptions  = useMemo(() => lengths.map((l) => ({ value: l.id, label: l.name })), [lengths]);
  const harvestOptions = useMemo(() => harvestTypes.map((h) => ({ value: h.id, label: h.name })), [harvestTypes]);
  const bunchOptions   = useMemo(
    () => bunchSizes.map((b) => ({ value: b.id, label: `${b.stems_count} stems`, count: b.stems_count })),
    [bunchSizes]
  );

  // =====================
  // HANDLERS
  // =====================
  const handleFarmSelect = useCallback((farm) => {
    setSelectedFarm(farm);
    setShowFarmSelector(false);
    setData(EMPTY_FORM);
    setLastCapture(null);
  }, []);

  const handleChange = useCallback((name, value) => {
    setData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleVarietyChange = useCallback((selectedOption) => {
    if (!selectedOption) {
      setData((prev) => ({ ...prev, variety_location_id: "", variety: "" }));
      return;
    }
    setData((prev) => ({
      ...prev,
      variety_location_id: selectedOption.value,
      variety:             selectedOption.variety_id,
    }));
  }, []);

  const handleChangeFarm = useCallback(() => {
    setShowFarmSelector(true);
    setSelectedFarm(null);
    setData(EMPTY_FORM);
    setLastCapture(null);
  }, []);

  // =====================
  // REACT-SELECT STYLES
  // =====================
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      background:   "#faf8f5",
      borderColor:  state.isFocused ? "#a92f6f" : "#e8e0d6",
      borderWidth:  "1.5px",
      borderRadius: "14px",
      padding:      "4px 10px",
      minHeight:    "54px",
      boxShadow:    state.isFocused ? "0 0 0 4px rgba(169, 47, 111, 0.08)" : "none",
      transition:   "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": { borderColor: state.isFocused ? "#a92f6f" : "#c9a96e" },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#fce8f0"
        : state.isFocused
        ? "#faf5ed"
        : "white",
      color:      state.isSelected ? "#a92f6f" : "#2c2420",
      fontWeight: state.isSelected ? "600" : "400",
      padding:    "14px 18px",
      cursor:     "pointer",
      borderBottom: "1px solid #f0ebe4",
      transition: "all 0.15s ease",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "16px",
      border: "1.5px solid #e8e0d6",
      boxShadow: "0 24px 64px rgba(44, 36, 32, 0.10)",
      overflow: "hidden",
      zIndex: 1000,
      marginTop: "8px",
      animation: "menuSlideIn 0.2s ease-out",
    }),
    placeholder: (base) => ({
      ...base,
      color:      "#b8a99a",
      fontStyle:  "italic",
      fontSize:   "0.95rem",
      fontWeight: "400",
    }),
    singleValue: (base) => ({
      ...base,
      color:      "#2c2420",
      fontWeight: "600",
      fontSize:   "0.95rem",
    }),
    input: (base) => ({ ...base, color: "#2c2420" }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? "#a92f6f" : "#c4b5a5",
      transition: "color 0.2s ease",
      "&:hover": { color: "#a92f6f" },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: "#c4b5a5",
      "&:hover": { color: "#a92f6f" },
    }),
  };

  // =====================
  // SUBMIT
  // =====================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!bunchingStart?.locked) return Swal.fire("Start time required", "Select and lock this farm’s bunching start time before recording captures.");

    if (!sessionOpen || !activeSessionId) {
      return Swal.fire("Session Closed", "You cannot capture data right now.");
    }

    const required = ["worker", "variety_location_id", "length", "harvest_type", "bunch_size", "bunch_count"];
    for (const field of required) {
      if (!data[field]) {
        return Swal.fire("Incomplete", "Please fill in all required fields.");
      }
    }

    const workerObj           = workers.find((w) => String(w.id) === String(data.worker)) || {};
    const selectedVarietyOpt  = varietyOptions.find((o) => String(o.value) === String(data.variety_location_id));
    const varietyObj          = varieties.find((v) => String(v.id) === String(data.variety)) || {};
    const lengthObj           = lengths.find((l) => String(l.id) === String(data.length)) || {};
    const harvestObj          = harvestTypes.find((h) => String(h.id) === String(data.harvest_type)) || {};
    const bunchObj            = bunchSizes.find((b) => String(b.id) === String(data.bunch_size)) || {};
    const totalStems          = parseInt(data.bunch_count || 0) * parseInt(bunchObj.stems_count || 0);

    const confirm = await Swal.fire({
      title: "Confirm Capture",
      
      showCancelButton: true,
      confirmButtonText: "Confirm & Save",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#a92f6f",
      cancelButtonColor: "#9c8a7d",
      width: "520px",
      customClass: {
        popup:         "garden-confirm-popup",
        title:         "garden-confirm-title",
        htmlContainer: "garden-confirm-html",
      },
      html: `
        <div class="garden-summary">
          <div class="garden-summary-meta">
            <span class="garden-badge">Session #${activeSessionId || "N/A"}</span>
            <span class="garden-time">${new Date().toLocaleString()}</span>
          </div>
          <div class="garden-summary-row">
            <div class="garden-label"> Worker</div>
            <div class="garden-value"><strong>${workerObj.payroll_number || "N/A"}</strong> — ${workerObj.full_name || "Unknown"}</div>
          </div>
          <div class="garden-summary-row">
            <div class="garden-label"> Variety</div>
            <div class="garden-value">${varietyObj.name || "Unknown"}</div>
            <div class="garden-sub">${selectedVarietyOpt?.sub || ""}</div>
          </div>
          <div class="garden-summary-grid">
            <div class="garden-summary-row">
              <div class="garden-label"> Length</div>
              <div class="garden-value">${lengthObj.name || "N/A"}</div>
            </div>
            <div class="garden-summary-row">
              <div class="garden-label"> Harvest Type</div>
              <div class="garden-value">${harvestObj.name || "N/A"}</div>
            </div>
          </div>
          <div class="garden-summary-grid">
            <div class="garden-summary-row">
              <div class="garden-label"> Stems/Bunch</div>
              <div class="garden-value">${bunchObj.stems_count || "N/A"}</div>
            </div>
            <div class="garden-summary-row">
              <div class="garden-label"> Bunches</div>
              <div class="garden-value accent">${data.bunch_count}</div>
            </div>
          </div>
          <div class="garden-summary-total">
            <div class="garden-label"> Total Stems</div>
            <div class="garden-mega">${totalStems.toLocaleString()}</div>
          </div>
        </div>
      `,
    });

    if (!confirm.isConfirmed) return;

    try {
      setIsSubmitting(true);

      const payload = {
        worker:      data.worker,
        variety:     data.variety,
        variety_location: data.variety_location_id,
        length:      data.length,
        harvest_type: data.harvest_type,
        bunch_size:  data.bunch_size,
        session:     activeSessionId,
        bunch_count: parseInt(data.bunch_count, 10),
        farm:        selectedFarm?.name || selectedFarm?.id,
      };

      await createCapture(payload);

      setLastCapture({
        workerName:  workerObj.full_name,
        totalStems,
        timestamp:   new Date(),
      });

      setSuccessPulse(true);
      setTimeout(() => setSuccessPulse(false), 1500);

      Swal.fire({
        title: "Capture saved",
        text: "Data captured successfully.",
        
        confirmButtonColor: "#a92f6f",
        timer: 1400,
        timerProgressBar: true,
        showConfirmButton: false,
      });

      setData(EMPTY_FORM);

    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to save data. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // =====================
  // LOADING STATE
  // =====================
  if (loading) {
    return (
      <div className="garden-loading" role="status">
        <div className="garden-spinner">
          
        </div>
        <p>Loading capture workspace…</p>
      </div>
    );
  }

  // =====================
  // FARM SELECTOR
  // =====================
  const renderFarmSelector = () => (
    <div className="garden-step garden-step-farm">
      <div className="garden-hero">
        <div className="hero-ring">
          
          
        </div>

        <h1 className="garden-title">Choose a farm</h1>
        <p className="garden-subtitle">Your selection keeps workers and varieties relevant to this capture.</p>
      </div>

      <div className="farm-meadow">
        {farms.map((farm, idx) => (
          <button
            key={farm.id}
            className="farm-tile"
            onClick={() => handleFarmSelect(farm)}
            style={{ animationDelay: `${idx * 0.08}s` }}
          >
            <div className="farm-tile-visual">
              <div className="farm-icon-wrap">
                
              </div>
              <div className="farm-glow-bg" />
            </div>

            <div className="farm-tile-content">
              <div className="farm-tile-name">{farm.name}</div>
              <div className="farm-tile-stats">
                <span className="stat-chip">
                  <span className="stat-dot" />
                  {farm.workerCount} workers
                </span>
                <span className="stat-chip">
                  <span className="stat-dot green" />
                  {farm.locationCount} varieties
                </span>
              </div>
            </div>

            <div className="farm-tile-arrow">Select farm
              
            </div>
          </button>
        ))}
      </div>

      {!sessionOpen && (
        <div className="garden-session-closed">
          
          <p>Session is currently closed. Data capture unavailable.</p>
        </div>
      )}
    </div>
  );

  // =====================
  // CAPTURE FORM
  // =====================
  const renderCaptureForm = () => (
    <div className={`garden-step garden-step-form ${successPulse ? "success-pulse" : ""}`}>

      {/* Farm Context Bar */}
      <div className="farm-context-bar">
        <div className="context-glass">
          <div className="context-farm">
            <div className="context-badge">
              
            </div>
            <div className="context-info">
              <span className="context-label">Active Farm</span>
              <span className="context-name">{selectedFarm?.name}</span>
            </div>
          </div>

          <div className="context-divider" />

          <div className="context-session">
            <span className="pulse-ring" />
            <span className="session-label">{sessionOpen ? `Session #${activeSessionId} open` : "Session closed"}</span>
          </div>

          <button className="context-change" onClick={handleChangeFarm}>
            
            <span>Change farm</span>
          </button>
        </div>
      </div>

      {!bunchingStart?.locked && <section className="bunching-start-lock" aria-label="Daily bunching start time">
        <div className="bunching-start-copy"><span className="bunching-start-kicker">TODAY’S SHIFT SETUP</span><strong>When does bunching begin?</strong><span>Choose this farm’s start time. It locks once for today. The bunching target covers 7.67 capture hours. The scheduled start–end clock includes lunch and tea breaks.</span></div>
        <div className="bunching-start-input">
          <label htmlFor="bunch-start-time"><span className="bunching-clock-icon" aria-hidden="true">◷</span> START TIME <small>EAST AFRICA TIME</small></label>
          <input id="bunch-start-time" type="time" value={startTimeInput} onChange={event => setStartTimeInput(event.target.value)} disabled={Boolean(bunchingStart?.error || startBusy)} />
          <div className="bunching-time-presets" aria-label="Quick time choices">
            {[["Now", new Intl.DateTimeFormat("en-GB", { timeZone: "Africa/Nairobi", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date())], ["07:00", "07:00"], ["08:00", "08:00"]].map(([label, value]) => <button key={label} type="button" className={startTimeInput === value ? "selected" : ""} onClick={() => setStartTimeInput(value)} disabled={Boolean(bunchingStart?.error || startBusy)}>{label}</button>)}
          </div>
          <button type="button" className="bunching-lock-button" onClick={lockBunchingStart} disabled={!startTimeInput || startBusy || Boolean(bunchingStart?.error)}>{startBusy ? "Locking start time…" : "Confirm start time"}<span aria-hidden="true"> →</span></button>
          {bunchingStart?.error && <span role="alert">Could not load this farm’s start-time status. Refresh to try again.</span>}
        </div>
      </section>}

      {/* Last Capture Toast */}
      {lastCapture && (
        <div className="last-capture-toast" role="status">
          
          <div className="toast-body">
            <div className="toast-title">Last Capture</div>
            <div className="toast-meta">
              {lastCapture.workerName} • {lastCapture.totalStems.toLocaleString()} stems •{" "}
              {lastCapture.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="garden-form">
        <div className="bunch-form-heading"><p>CAPTURE DETAILS</p><h2>Record a bunch entry</h2><span>Complete all fields, then review before saving.</span></div>

        {/* Worker */}
        <div className="garden-field">
          <label className="garden-label">
            
            Worker
            <span className="label-count">{filteredWorkers.length} available</span>
          </label>
          <Select
            aria-label="Worker"
            components={textSelectComponents}
            options={workerOptions}
            value={workerOptions.find((o) => String(o.value) === String(data.worker)) || null}
            onChange={(s) => handleChange("worker", s?.value || "")}
            placeholder="Select worker…"
            isSearchable
            isClearable
            styles={selectStyles}
            formatOptionLabel={(option) => (
              <div className="custom-option">
                <div className="custom-option-main">{option.label}</div>
                {option.sub && <div className="custom-option-sub">{option.sub}</div>}
              </div>
            )}
          />
        </div>

        {/* Variety & Location */}
        <div className="garden-field">
          <label className="garden-label">
            
            Variety &amp; Location
            <span className="label-count">{filteredVarietyLocations.length} available</span>
          </label>
          <Select
            aria-label="Variety and location"
            components={textSelectComponents}
            options={varietyOptions}
            value={varietyOptions.find((o) => String(o.value) === String(data.variety_location_id)) || null}
            onChange={handleVarietyChange}
            placeholder="Select variety…"
            isSearchable
            isClearable
            styles={selectStyles}
            formatOptionLabel={(option) => (
              <div className="custom-option">
                <div className="custom-option-main">{option.label}</div>
                <div className="custom-option-sub">
                  {option.sub} • {option.stems} stems/net
                </div>
              </div>
            )}
            noOptionsMessage={() => "No varieties found for this farm"}
          />
        </div>

        {/* Length & Harvest Type */}
        <div className="garden-row">
          <div className="garden-field">
            <label className="garden-label">
              
              Length
            </label>
            <Select
            aria-label="Length"
            components={textSelectComponents}
              options={lengthOptions}
              value={lengthOptions.find((o) => String(o.value) === String(data.length)) || null}
              onChange={(s) => handleChange("length", s?.value || "")}
              placeholder="Select…"
              isClearable
              styles={selectStyles}
            />
          </div>

          <div className="garden-field">
            <label className="garden-label">
              
              Harvest Type
            </label>
            <Select
            aria-label="Harvest type"
            components={textSelectComponents}
              options={harvestOptions}
              value={harvestOptions.find((o) => String(o.value) === String(data.harvest_type)) || null}
              onChange={(s) => handleChange("harvest_type", s?.value || "")}
              placeholder="Select…"
              isClearable
              styles={selectStyles}
            />
          </div>
        </div>

        {/* Bunch Size & Count */}
        <div className="garden-row">
          <div className="garden-field">
            <label className="garden-label">
              
              Stems / Bunch
            </label>
            <Select
            aria-label="Stems per bunch"
            components={textSelectComponents}
              options={bunchOptions}
              value={bunchOptions.find((o) => String(o.value) === String(data.bunch_size)) || null}
              onChange={(s) => handleChange("bunch_size", s?.value || "")}
              placeholder="Select…"
              isClearable
              styles={selectStyles}
            />
          </div>

          <div className="garden-field">
            <label className="garden-label">
              
              Bunch Count
            </label>
            <div className="garden-counter">
              <button
                type="button"
                className="counter-btn"
                onClick={() =>
                  setData((p) => ({
                    ...p,
                    bunch_count: Math.max(0, parseInt(p.bunch_count || 0) - 1),
                  }))
                }
              >Less</button>
              <input
                type="number"
                aria-label="Bunch count"
                inputMode="numeric"
                min="1"
                value={data.bunch_count}
                onChange={(e) => handleChange("bunch_count", e.target.value)}
                className="counter-input"
                placeholder="0"
              />
              <button
                type="button"
                className="counter-btn"
                onClick={() =>
                  setData((p) => ({
                    ...p,
                    bunch_count: parseInt(p.bunch_count || 0) + 1,
                  }))
                }
              >More</button>
            </div>
          </div>
        </div>

        {/* Live Stem Preview */}
        {data.bunch_size && data.bunch_count && (
          <div className="garden-preview">
            <div className="preview-visual">
              <div className="preview-ring">
                <div className="preview-stems">
                  {(
                    parseInt(data.bunch_count || 0) *
                    parseInt(bunchOptions.find((b) => String(b.value) === String(data.bunch_size))?.count || 0)
                  ).toLocaleString()}
                </div>
              </div>
            </div>
            <div className="preview-label">Total Stems</div>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          className="garden-submit"
          disabled={isSubmitting || !sessionOpen || !bunchingStart?.locked}
        >
          <span className="submit-shine" />
          <span className="submit-text">
            {isSubmitting ? "Saving…" : "Save Capture"}
          </span>
          
        </button>

      </form>
    </div>
  );

  // =====================
  // MAIN RENDER
  // =====================
  return (
    <div className="garden-universe">
      <div className="garden-bg">
        <div className="bg-orb orb-1" />
        <div className="bg-orb orb-2" />
        <div className="bg-orb orb-3" />
        <div className="bg-grain" />
      </div>

      <div className="garden-container">
        <header className="bunch-design-header">
          <div><p>SMARTHARVEST / PACKHOUSE</p><h1>Buncher capture</h1><span>Every bunch, carefully accounted for.</span></div>
          <div className="bunch-design-mark" aria-hidden="true">01<span>CAPTURE WORKSPACE</span></div>
        </header>
        {showFarmSelector ? renderFarmSelector() : renderCaptureForm()}
      </div>
    </div>
  );
};

export default CapturePage;
